import { useCallback, useState } from "react";

import { Form, Typography } from "antd";
import { clsx } from "clsx";

import {
    acceptedRegalDateFormatLink,
    RVLearnMoreLink,
    RVFormItem,
    RVSimpleTextFloatingPlaceholder,
    ConfigurableVariablesPickerPlugin,
    TypeaheadOptionsPickerPlugin,
    CustomVariablesHighlightPlugin,
    PlainTextEditor,
} from "@regal-voice/ui";
import { Row } from "Components/shared/Flexbox";
import { useTriggerEventNode } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/shared/hooks/useTriggerEventNode";

import { FIELD_TYPE_STRING, FIELD_TYPE_DATE, LOCAL_TIME_HANDLEBARS_WRAP } from "../../../../../../constants";
import { useUpdateProfileLexicalOptions } from "./Hooks/useUpdateProfileLexicalOptions/useUpdateProfilesLexicalOptions";

import styles from "./UpdateContactLexicalInput.module.scss";

// @todo replace with @regal-voice/ui/components/organisms/RVLexicalEditor/utils/constants
export const HANDLEBAR_TRIGGERS = [{ openingTag: "{{", closingTag: "}}" }];
const HANDLEBARS_WRAP = /\{\{.*?\}\}/;
const HANDLEBARS_REGEX_LIST = [LOCAL_TIME_HANDLEBARS_WRAP, HANDLEBARS_WRAP];

type InputProps = {
    onChange: (value: string) => void;
    currentSchema: string;
    fieldType: string;
    name: Array<string>;
    rules?: Array<any>;
    label: string;
    isSingleLine: boolean;
};

export function UpdateContactLexicalInput({
    onChange,
    currentSchema,
    fieldType,
    name,
    rules,
    label,
    isSingleLine,
}: InputProps) {
    const isStringField = fieldType === FIELD_TYPE_STRING;
    const { triggerNode } = useTriggerEventNode();

    const text = Form.useWatch(name);

    const { options: lexicalOptions, schemaOptions } = useUpdateProfileLexicalOptions({
        currentSchema,
        triggeringEvent: triggerNode,
        filterType: fieldType,
    });

    const [isTextEditorEmpty, setIsTextEditorEmpty] = useState(!text);
    const [displayFloatingLabel, setDisplayFloatingLabel] = useState(!!text);

    const onEditorFocus = useCallback(() => {
        setDisplayFloatingLabel(isTextEditorEmpty);
    }, [isTextEditorEmpty]);

    const onEditorBlur = useCallback(() => {
        setDisplayFloatingLabel(!isTextEditorEmpty);
    }, [isTextEditorEmpty]);

    const onValueChange = useCallback(
        (value: string) => {
            // remove any potential leading or trailing whitespace
            onChange(value.trim());
            setIsTextEditorEmpty(!value);
        },
        [onChange]
    );

    return (
        <RVFormItem>
            <div
                className={clsx(styles.textAreaWrapper, {
                    [styles.singleLine]: isSingleLine,
                    [styles.multiLine]: !isSingleLine,
                    [styles.floatingPlaceholder]: displayFloatingLabel,
                })}
            >
                <RVSimpleTextFloatingPlaceholder
                    placeholderText={label}
                    displayFloatingLabel={displayFloatingLabel}
                    isTextEditorEmpty={isTextEditorEmpty}
                    isSingleLine={isSingleLine}
                />
                <div className={clsx(styles.lexicalContainer)}>
                    <RVFormItem noStyle name={name} rules={rules} validateTrigger={["onSubmit"]}>
                        <PlainTextEditor
                            singleRow={isSingleLine && !isStringField}
                            data-testid="update-contactnode-lexical-editor"
                            className={styles.editorHasContainer}
                            inputClassName={styles.inputHasContainer}
                            formItemName={name}
                            onFocus={onEditorFocus}
                            onBlur={onEditorBlur}
                            onChange={onValueChange}
                        >
                            {FIELD_TYPE_STRING === fieldType && (
                                // this component will clear out the values when when the user selects a trigger
                                // so if we need this work WITH ConfigurableVariablesPickerPlugin we will need to
                                // mediate that
                                <TypeaheadOptionsPickerPlugin
                                    denylist={[HANDLEBAR_TRIGGERS[0].openingTag, HANDLEBAR_TRIGGERS[0].closingTag]}
                                    popAbove
                                    loading={false}
                                    fixedWidth
                                    allOptions={schemaOptions}
                                ></TypeaheadOptionsPickerPlugin>
                            )}
                            <ConfigurableVariablesPickerPlugin
                                popAbove
                                loading={false}
                                fixedWidth
                                prioritizedTriggers={HANDLEBAR_TRIGGERS}
                                options={lexicalOptions}
                            />

                            <CustomVariablesHighlightPlugin prioritiziedRegexList={HANDLEBARS_REGEX_LIST} />
                        </PlainTextEditor>
                    </RVFormItem>
                </div>
            </div>
            <>
                {FIELD_TYPE_DATE === fieldType && (
                    <Row className={styles.timeInfo}>
                        <Typography.Text>See datetime formats accepted&nbsp;</Typography.Text>
                        <RVLearnMoreLink text="here." href={acceptedRegalDateFormatLink} />
                    </Row>
                )}
            </>
        </RVFormItem>
    );
}
